// hooks/useLiveBroadcast.js
import { useState, useRef } from 'react';
import { useSocial, useLiveSession } from '../contexts/SocialContext';
import { useToast } from '../contexts/ToastContext';
import { getAudioSettings } from './useAudioSettings';

export const useLiveBroadcast = (planData, defaultTitle, { isCasting = false } = {}) => {
  const liveCtx = useLiveSession();
  const social = useSocial();
  const [isGoingLive, setIsGoingLive] = useState(false);
  const lastSyncRef = useRef(0);
  const toast = useToast();

  // Going live is a state change with no undo prompt and, when it fails, no
  // visible consequence: the screen you are already on is exactly the screen a
  // failure leaves you on. Silence therefore reads as success, and you keep
  // training under the impression you are broadcasting — which is how a solo
  // Ready gate turned up in what its owner believed was a live session. Say so.
  const reportFailure = (err) => {
    console.error("Go live failed:", err);
    toast?.showToast?.({
      title: "Couldn't go live",
      description: "Still training on your own — check your connection and try again.",
      variant: "destructive",
    });
  };

  // privacy: "friends" (default) | "public" | "invite_only" — who may JOIN and
  // train with you; the join endpoint enforces it server-side.
  //
  // mode: "together" (default) | "broadcast" — a different question, which is
  // why it is not another privacy value. Privacy narrows the set of people who
  // can take part; broadcast says nobody takes part at all, however wide the
  // privacy setting is. A broadcast always gets a watch link.
  //
  // allowWatchers: on a "together" session, additionally mint a watch link so
  // people without accounts can follow along beside the participants.
  const goLive = async (
    privacy = "friends",
    { mode = "together", allowWatchers = false } = {},
  ) => {
    if (!liveCtx) return;
    setIsGoingLive(true);
    try {
      await liveCtx.createLive({
        title: planData?.label || defaultTitle || "Live workout",
        plan_data: planData,
        privacy,
        mode,
        allow_watchers: allowWatchers,
      });
      return true;
    } catch (err) {
      reportFailure(err);
      return false;
    } finally {
      setIsGoingLive(false);
    }
  };

  // Go live from where a solo workout has already got to (#81).
  //
  // Ordering matters and is not cosmetic. createLive is awaited first and
  // nothing is torn down before it resolves, so a failure there leaves the
  // person on their still-running solo session rather than a dead screen.
  // Once it succeeds the live session exists and CoachView has already swapped
  // to the follower, so `onHandover` fires regardless of whether the start
  // itself lands: the local engine has to stop either way, or it keeps ticking
  // underneath — advancing phases and firing cues behind the live session.
  const goLiveFromHere = async (
    privacy = "friends",
    {
      mode = "together",
      allowWatchers = false,
      timeline,
      atIndex = 0,
      priorPhaseLog = null,
      changeoverHold,
      onHandover,
    } = {},
  ) => {
    if (!liveCtx || !Array.isArray(timeline) || timeline.length === 0) {
      return false;
    }
    setIsGoingLive(true);
    try {
      await liveCtx.createLive(
        {
          title: planData?.label || defaultTitle || "Live workout",
          plan_data: planData,
          privacy,
          mode,
          allow_watchers: allowWatchers,
        },
        { priorPhaseLog },
      );
      onHandover?.();
      await liveCtx.startLiveWhenJoined(
        timeline,
        { countInSeconds: getAudioSettings().countInDuration, changeoverHold },
        { atIndex },
      );
      return true;
    } catch (err) {
      // A create failure leaves the solo session untouched; a start failure
      // leaves a session sitting in its lobby, which the Start button there
      // can still rescue. Either way the workout carries on, so the only thing
      // that can go wrong unnoticed is the going-live part — hence the toast.
      reportFailure(err);
      return false;
    } finally {
      setIsGoingLive(false);
    }
  };

  // Cast-only broadcast: relays coach state to the user's own hardware
  // devices via the /live socket so they drift-correct against the web
  // app's clock. Live sessions no longer stream coach state — the server's
  // timeline runner broadcasts authoritative snapshots instead (#28).
  const syncState = (stateData) => {
    if (!isCasting) return;

    const now = Date.now();
    if (now - lastSyncRef.current < 1000) return; // Throttle to 1 Hz
    lastSyncRef.current = now;

    if (social?.castSync) {
      social.castSync({
        status: stateData?.status,
        path: stateData?.path,
        timeLeft: stateData?.timeLeft,
        currentNode: stateData?.currentNode,
        nextNode: stateData?.nextNode,
      });
    }
  };

  const endLive = async () => {
    if (!liveCtx?.endLive) return;
    await liveCtx.endLive();
  };

  return {
    isLive: !!liveCtx?.liveSession?.session,
    isHost: !!liveCtx?.liveSession?.isHost,
    isGoingLive,
    goLive,
    goLiveFromHere,
    syncState,
    endLive,
  };
};